
import angular from 'angular';
import { CapabilityIdEnum } from 'src/app/shared/capabilities-and-permissions';

SearchSelectorsFactory.$inject = ['$filter', '$translate', 'StrategiesFactory', 'LocalStorageFactory', 'AngularSitesService'];
export function SearchSelectorsFactory($filter, $translate, StrategiesFactory, LocalStorageFactory, AngularSitesService) {


  function getSitesWithSearchCapability(capabilities) {
    return Object.keys(capabilities || {})
      .filter(siteId => capabilities[siteId]?.includes(CapabilityIdEnum.Answer))
      .map(siteId => AngularSitesService.sitesV2Map.get(+siteId));
  }


  function createBaseDayRangeSelector() {
    function defaultValue() {
      let from = new Date();
      let to = new Date();
      from.setDate(from.getDate() - 7);
      return {
        from: from,
        to: to
      };
    }

    let res = {
      value: defaultValue(),
      isOpen: false,
      focus: 'from',
      date: null,
      type: 'dayrange',
      onToggle: function (open) {
        if (open) {
          this.focus = 'from';
          this.date = this.value.from;
        }
      },
      onFromClick: function () {
        this.focus = 'from';
      },
      onToClick: function () {
        this.focus = 'to';
      },
      dayRangeChanged: function () {
        if (this.focus === 'from') {
          this.value.from = this.date;
          if (this.value.to < this.value.from) {
            this.value.to = this.date;
          }
          this.focus = 'to';
        } else {
          this.value.to = this.date;
          this.focus = 'from';
        }
      },
      appliedValues: [],
      setAppliedValues: function getAppliedSelector() {
        if (this.value && (this.value.to || this.value.from)) {
          res = [];
          if (this.value.from) {
            res.push({
              id: this.id + '-from',
              // do not use pipe dateTimezone until the picker deal with timezone (in angular migration)
              description: 'from ' + $filter('date')(this.value.from),
              value: this.value.from
            });
          }
          if (this.value.to) {
            res.push({
              id: this.id + '-to',
              // do not use pipe dateTimezone until the picker deal with timezone (in angular migration)
              description: 'to ' + $filter('date')(this.value.to),
              value: this.value.to
            });
          }
          this.appliedValues = res;
        }
        else {
          this.appliedValues = null;
        }
      },
      setValuesFromAppliedValues: function setValuesFromAppliedValues() {
        if (!this.appliedValues) {
          this.value = defaultValue();
          return;
        }
        for (let value of this.appliedValues) {
          if (value.id === this.id + '-to') {
            this.value.to = value.value;
          } else if (value.id === this.id + '-from') {
            this.value.from = value.value;
          }
        }
      },
      remove: function remove() {
        this.appliedValues = null;
        this.value = defaultValue();
      },
      removeValue: function removeValue(value) {
        if (value.id === this.id + '-to') {
          this.value.to = null;
          this.setAppliedValues();
        } else if (value.id === this.id + '-from') {
          this.value.from = null;
          this.setAppliedValues();
        }
      },
      save: function save() {
        LocalStorageFactory.setItem('searchSelectorValues.' + this.id, this.appliedValues);
      },
      load: function load() {
        let savedData = LocalStorageFactory.getItem('searchSelectorValues.' + this.id);
        if (!savedData) {
          return;
        }
        let appliedValues = angular.copy(savedData);
        // Date objects are json serialized as strings that needs to be converted into date objects manually on unserialization.
        for (let value of appliedValues) {
          value.value = new Date(value.value);
        }
        this.appliedValues = appliedValues;
        this.setValuesFromAppliedValues();
      }
    };

    res.dateOptions = {
      datepickerMode: 'day',
      showWeeks: false,
      maxDate: Date.now(),
      customClass: function (item) {
        if (item.mode === 'day') {
          if (this.focus === 'from') {
            if (item.date < this.value.from) {
              return 'outside';
            } else if (item.date > this.value.to) {
              return 'outside';
            } else {
              return 'inside';
            }
          } else {
            if (item.date > this.value.to) {
              return 'outside';
            } else if (item.date < this.value.from) {
              return 'outside';
            } else {
              return 'inside';
            }
          }
        }
      }.bind(res),
      dateDisabled: function (item) {
        if (this.focus === 'from') {
          return false;
        } else {
          return item.date < this.value.from;
        }
      }.bind(res)
    };

    return res;
  }


  function createBaseCheckboxesWithChildrenSelector() {
    let selector = {
      type: 'checkboxesWithChildren',
      appliedValues: [],
      toggleCheckboxGroup: function selectChildren(item) {
        let nSelected = item.children.filter(c => c.value).length;
        if (item.value || nSelected) {
          for (let child of item.children) {
            child.value = false;
          }
          item.value = false;
        } else {
          for (let child of item.children) {
            child.value = true;
          }
          item.value = true;
        }
      },
      onToggleChild: function onToggleChild(item) {
        let nSelected = item.children.filter(c => c.value).length;
        item.value = nSelected === item.children.length;
      },
      remove: function remove() {
        this.appliedValues = null;
        for (let item of this.items) {
          item.value = false;
          for (let child of item.children) {
            child.value = false;
          }
        }
      },
      removeValue: function removeValue(value) {
        for (let item of this.items) {
          if (item.id === value.id) {
            item.value = false;
            for (let child of item.children) {
              child.value = false;
            }
            this.setAppliedValues();
          }
        }
      },
      save: function save() {
        LocalStorageFactory.setItem('searchSelectorValues.' + this.id, this.appliedValues);
      },
      load: function load() {
        let savedAppliedValues = LocalStorageFactory.getItem('searchSelectorValues.' + this.id);
        if (!savedAppliedValues) {
          return;
        }

        let newAppliedValues = [];

        //Keeping only valid selector values (each site must still be here)
        for (let value of savedAppliedValues) {
          for (let item of this.items) {
            // To prevent bugs if items data change.
            if (item.id === value.id && angular.equals(item.data, value.data)) {
              newAppliedValues.push(value);
            }
          }
        }
        this.appliedValues = newAppliedValues;
        this.setValuesFromAppliedValues();
      }

    };

    return selector;
  }

  function createBaseCheckboxesSelector() {
    let selector = {
      type: 'checkboxes',
      appliedValues: [],
      setAppliedValues: function getAppliedSelector() {
        this.appliedValues = this.items.filter(i => i.value)
          .map(i => ({
            id: i.id,
            data: i.data,
            value: i.value,
            description: i.name
          }));
      },
      setValuesFromAppliedValues: function setValuesFromAppliedValues() {
        if (!this.appliedValues) {
          this.value = null;
          return;
        }
        for (let value of this.appliedValues) {
          for (let item of this.items) {
            if (value.id === item.id) {
              item.value = value.value;
            }
          }
        }
      },
      remove: function remove() {
        this.appliedValues = null;
        for (let item of this.items) {
          item.value = false;
        }
      },
      removeValue: function removeValue(value) {
        for (let item of this.items) {
          if (item.id === value.id) {
            item.value = false;
            this.setAppliedValues();
          }
        }
      },
      save: function save() {
        LocalStorageFactory.setItem('searchSelectorValues.' + this.id, this.appliedValues);
      },
      load: function load() {
        let savedAppliedValues = LocalStorageFactory.getItem('searchSelectorValues.' + this.id);
        if (!savedAppliedValues) {
          return;
        }

        let newAppliedValues = [];

        //Checking the selector hasn't changed (for example, tag deletion/edition)
        for (let value of savedAppliedValues) {
          for (let item of this.items) {
            // To prevent bugs if items data change.
            if (item.id === value.id && angular.equals(item.data, value.data)) {
              newAppliedValues.push(value);
            }
          }
        }
        this.appliedValues = newAppliedValues;
        this.setValuesFromAppliedValues();
      }
    };

    return selector;
  }

  function createBaseTextsSelector() {
    return {
      type: 'texts',
      appliedValues: [],
      setAppliedValues: function getAppliedSelector() {
        this.appliedValues = this.items.filter(i => i.value)
          .map(i => ({
            id: i.id,
            value: i.value,
            description: i.name + ' ' + i.value
          }));
      },
      setValuesFromAppliedValues: function setValuesFromAppliedValues() {
        if (!this.appliedValues) {
          this.value = null;
          return;
        }
        for (let value of this.appliedValues) {
          for (let item of this.items) {
            if (value.id === item.id) {
              item.value = value.value;
            }
          }
        }
      },
      remove: function remove() {
        this.appliedValues = null;
        for (let item of this.items) {
          item.value = '';
        }
      },
      removeValue: function removeValue(value) {
        for (let item of this.items) {
          if (item.id === value.id) {
            item.value = '';
            this.setAppliedValues();
          }
        }
      },
      save: function save() {
        LocalStorageFactory.setItem('searchSelectorValues.' + this.id, this.appliedValues);
      },
      load: function load() {
        let savedAppliedValues = LocalStorageFactory.getItem('searchSelectorValues.' + this.id);
        if (!savedAppliedValues) {
          return;
        }

        let newAppliedValues = [];

        // Keeping only valid values
        for (let value of savedAppliedValues) {
          for (let item of this.items) {
            if (item.id === value.id) {
              newAppliedValues.push(value);
            }
          }
        }
        this.appliedValues = newAppliedValues;
        this.setValuesFromAppliedValues();
      }
    };
  }

  return {

    getSitesWithSearchCapability: getSitesWithSearchCapability,


    createReceptionDate() {

      let specificSelectorFragment = {
        id: 'receptionDateDayRange',
        name: $translate.instant('search/selectors/reception_date'),
        createParametersFragment() {
          if (!this.appliedValues) {
            return {};
          }
          let res = {};
          for (let val of this.appliedValues) {
            if (val.id === this.id + '-from') {
              val.value.setUTCHours(0);
              val.value.setUTCMinutes(0);
              val.value.setUTCSeconds(0);
              res.since = val.value;
            }
            if (val.id === this.id + '-to') {
              val.value.setUTCHours(23);
              val.value.setUTCMinutes(59);
              val.value.setUTCSeconds(59);
              res.until = val.value;
            }
          }
          return res;
        }
      };

      return angular.extend(createBaseDayRangeSelector(), specificSelectorFragment);
    },

    createAccountsAndChannelsSelector(capabilities) {
      let specificSelectorFragment = {
        id: 'accoundAndChannel',
        name: $translate.instant('search/selectors/accounts_and_channels'),
        setAppliedValues: function getAppliedSelector() {
          this.appliedValues = this.items
            .map(function (item) {
              let res = {
                id: item.id
              };
              res.value = item.children
                .filter(c => c.value)
                .map(c => c.data.taskTypeId);
              let description = item.name + ' : ';
              description += item.children
                .filter(c => c.value)
                .reduce((acc, c) => acc + $translate.instant('task/' + c.data.taskTypeString) + ', ', '');
              res.description = description.slice(0, -2);
              return res;
            })
            .filter(item => item.value.length);
        },
        setValuesFromAppliedValues: function setValuesFromAppliedValues() {
          for (let value of this.appliedValues) {
            for (let item of this.items) {
              if (item.id === value.id) {
                for (let child of item.children) {
                  let foundTaskTypeId = value.value.find(taskTypeId => taskTypeId === child.data.taskTypeId);
                  child.value = !!foundTaskTypeId;
                }
                //Set item.value
                this.onToggleChild(item);
              }
            }
          }
        },
        createParametersFragment: function createParametersFragment() {
          if (!this.appliedValues) {
            return {};
          }
          let siteIdsQueueTypes = [];
          for (let val of this.appliedValues) {
            for (let queueType of val.value) {
              siteIdsQueueTypes.push([val.id, queueType]);
            }
          }
          let res = siteIdsQueueTypes.length ? { sites_ids_queues_types: siteIdsQueueTypes } : {};
          return res;
        }
      };

      const sites = getSitesWithSearchCapability(capabilities);

      specificSelectorFragment.items = sites.map(function (site) {
        let strategy = StrategiesFactory.getStrategyFromType(site.type);

        // deleted site but account still exists
        if(strategy) {
          return {
            id: site.id,
            name: site.name,
            value: false,
            is_open: true,
            children: strategy.getAllTasksTypesForSearchAndMetrics()
              .map(t => ({
                id: site.id + '-' + t,
                data: { taskTypeId: t, taskTypeString: strategy.getTaskTypeString(t) },
                value: false,
                name: $translate.instant('task/' + strategy.getTaskTypeString(t))
              }))
          };
        }
      });

      return angular.extend(createBaseCheckboxesWithChildrenSelector(), specificSelectorFragment);
    },

    createStatusSelector() {

      let specificSelectorFragment = {
        id: 'status',
        name: $translate.instant('task/state'),
        createParametersFragment: function createParametersFragment() {
          if (!this.appliedValues) {
            return {};
          }
          let states = [];
          for (let val of this.appliedValues) {
            states.push(val.id);
          }
          let res = states.length ? { states: states } : {};
          return res;
        }
      };

      specificSelectorFragment.items = [
        { id: 'pending', value: false, name: $translate.instant('task/state/pending'), data: null },
        { id: 'assigned', value: false, name: $translate.instant('task/state/assigned'), data: null },
        { id: 'answered', value: false, name: $translate.instant('task/state/answered'), data: null },
        { id: 'archived', value: false, name: $translate.instant('task/state/archived'), data: null },
        { id: 'on_hold', value: false, name: $translate.instant('task/state/on_hold'), data: null },
        { id: 'help_requested', value: false, name: $translate.instant('task/state/help_requested'), data: null },
        { id: 'help_answered', value: false, name: $translate.instant('task/state/help_answered'), data: null },
        { id: 'archived_auto', value: false, name: $translate.instant('task/state/archived_auto'), data: null }
      ];

      return angular.extend(createBaseCheckboxesSelector(), specificSelectorFragment);
    },

    createMessageAuthorSelector() {
      let specificSelectorFragment = {
        id: 'author-name',
        name: $translate.instant('task/message_author'),
        items: [
          {
            id: 'author-name-is',
            name: $translate.instant('common/name'),
            value: ''
          }
        ],
        createParametersFragment: function createParametersFragment() {
          if (!this.appliedValues || !this.appliedValues.length) {
            return {};
          }
          let res = this.appliedValues[0].value ? { message_author: this.appliedValues[0].value } : {};
          return res;
        }
      };
      return angular.extend(createBaseTextsSelector(), specificSelectorFragment);
    },

    createMessageContentSelector() {
      let specificSelectorFragment = {
        id: 'message-content',
        name: $translate.instant('task/message_received'),
        items: [
          {
            id: 'message-content-includes',
            name: $translate.instant('search/selectors/message_content/including'),
            value: ''
          },
          {
            id: 'message-content-excludes',
            name: $translate.instant('search/selectors/message_content/excluding'),
            value: ''
          }
        ],
        createParametersFragment: function createParametersFragment() {
          if (!this.appliedValues) {
            return {};
          }
          let res = {};
          for (let val of this.appliedValues) {
            if (val.id === 'message-content-includes' && val.value) {
              res.message_includes = val.value;
            }
            if (val.id === 'message-content-excludes' && val.value) {
              res.message_excludes = val.value;
            }
          }
          return res;
        },
      };
      return angular.extend(createBaseTextsSelector(), specificSelectorFragment);
    },

    createTagsSelectors(company) {
      let selectors = [];
      for (let [index, tg] of company.tags_groups.entries()) {
        let specificSelectorFragment = {
          id: 'tagGroup' + tg.id,
          tagGroupIndex: tg.tag_index,
          type: 'checkboxes',
          name: tg.name,
          items: tg.tags.map(function (tag) {
            return {
              id: 'tag' + tag.id,
              data: tag.id,
              name: tag.name,
              value: false
            };
          }),
          createParametersFragment: function createParametersFragment(parameters) {
            if (!this.appliedValues) {
              return {};
            }
            let res = {};
            let groups_tags_ids = parameters.groups_tags_ids || [];
            let tagIdsByGroup = [];
            let tagIds = this.appliedValues.filter(val => val.value).map(val => val.data);
            if (tagIds.length) {
              tagIdsByGroup.push(this.tagGroupIndex);
              tagIdsByGroup.push(tagIds);
              groups_tags_ids.push(tagIdsByGroup);
              res.groups_tags_ids = groups_tags_ids;
            }
            return res;
          },
        };
        let completeSelector = angular.extend(createBaseCheckboxesSelector(), specificSelectorFragment);
        selectors.push(completeSelector);
      }
      return selectors;
    },

    createActionDateSelector() {

      let specificSelectorFragment = {
        id: 'actionDateDayRange',
        name: $translate.instant('search/selectors/action_date'),
        createParametersFragment() {
          if (!this.appliedValues) {
            return {};
          }
          let res = {};
          for (let val of this.appliedValues) {
            if (val.id === this.id + '-from') {
              val.value.setUTCHours(0);
              val.value.setUTCMinutes(0);
              val.value.setUTCSeconds(0);
              res.action_date_since = val.value;
            }
            if (val.id === this.id + '-to') {
              val.value.setUTCHours(23);
              val.value.setUTCMinutes(59);
              val.value.setUTCSeconds(59);
              res.action_date_until = val.value;
            }
          }
          return res;
        }
      };

      return angular.extend(createBaseDayRangeSelector(), specificSelectorFragment);
    },

    createActionTypeSelector() {

      let specificSelectorFragment = {
        id: 'actionType',
        name: $translate.instant('search/selectors/action_type'),
        createParametersFragment: function createParametersFragment() {
          if (!this.appliedValues) {
            return {};
          }
          let actionTypes = [];
          for (let val of this.appliedValues) {
            actionTypes.push(val.data);
          }
          let res = actionTypes.length ? { action_types: actionTypes } : {};
          return res;
        }
      };


      specificSelectorFragment.items = [
        { id: 'archived', value: false, name: $translate.instant('search/selectors/action_type/archived'), data: 0 },
        { id: 'answered', value: false, name: $translate.instant('search/selectors/action_type/answered'), data: 1 },
        { id: 'auto_archived', value: false, name: $translate.instant('search/selectors/action_type/archived_auto'), data: 2 },
        { id: 'comment', value: false, name: $translate.instant('search/selectors/action_type/comment'), data: 16 },
        { id: 'help_request', value: false, name: $translate.instant('search/selectors/action_type/help_request'), data: 17 },
        { id: 'help_answer', value: false, name: $translate.instant('search/selectors/action_type/help_answer'), data: 18 },
        { id: 'acquired', value: false, name: $translate.instant('search/selectors/action_type/acquired'), data: 19 },
        { id: 'released', value: false, name: $translate.instant('search/selectors/action_type/released'), data: 20 },
        { id: 'steal', value: false, name: $translate.instant('search/selectors/action_type/steal'), data: 21 },
        { id: 'assigned_to', value: false, name: $translate.instant('search/selectors/action_type/assigned_to'), data: 27 },
        { id: 'tag', value: false, name: $translate.instant('search/selectors/action_type/tag'), data: 33 },
        { id: 'on_hold', value: false, name: $translate.instant('search/selectors/action_type/on_hold'), data: 37 },

        //TODO: are these the only types we want?
      ];

      return angular.extend(createBaseCheckboxesSelector(), specificSelectorFragment);
    },


    createActionAuthorSelector(adminUsers) {
      let specificSelectorFragment = {
        id: 'actionAuthors',
        name: $translate.instant('search/selectors/action_author'),
        setAppliedValues: function getAppliedSelector() {
          this.appliedValues = this.items
            .map(function (item) {
              let res = { id: item.id };
              res.value = item.children
                .filter(c => c.value)
                .map(c => c.id);
              let description = item.name + ' : ';
              description += item.children
                .filter(c => c.value)
                .reduce((acc, c) => acc + c.name + ', ', '');
              res.description = description.slice(0, -2);
              return res;
            })
            .filter(item => item.value.length);
        },
        setValuesFromAppliedValues: function setValuesFromAppliedValues() {
          for (let value of this.appliedValues) {
            for (let item of this.items) {
              if (item.id === value.id) {
                for (let child of item.children) {
                  let foundTaskTypeId = value.value.find(adminUserId => adminUserId === child.id);
                  child.value = !!foundTaskTypeId;
                }
                //Set item.value
                this.onToggleChild(item);
              }
            }
          }
        },
        createParametersFragment: function createParametersFragment() {
          if (!this.appliedValues) {
            return {};
          }
          let allAdminUsers = this.appliedValues.reduce(function (acc, users) { return acc.concat(users.value || []); }, []);
          let res = allAdminUsers.length ? { action_author_ids: allAdminUsers } : {};
          return res;
        }

      };

      specificSelectorFragment.items = [
        {
          id: 'bots',
          name: $translate.instant('metrics/users/bots'),
          value: false,
          children: adminUsers.admin_users_bots.map(au => ({ id: au.id, name: au.name, value: false, data: null })),
          is_open: true
        },
        {
          id: 'humans',
          name: $translate.instant('metrics/users/humans'),
          value: false,
          children: adminUsers.admin_users_humans.map(au => ({ id: au.id, name: au.name, value: false, data: null })),
          is_open: true
        }
      ];

      return angular.extend(createBaseCheckboxesWithChildrenSelector(), specificSelectorFragment);
    },


  };

}










